import _ from "lodash";
import { RpcRequest } from "./types";
import moment from "moment";

export class Connection {
  private socket: WebSocket | null = null;
  public status: number = WebSocket.CONNECTING;
  private responseBuffer: string[] = [];
  private responseBufferContentLength = 0;

  private clearResponseBuffer() {
    this.responseBuffer = [];
    this.responseBufferContentLength = 0;
  }

  private tries = 0;

  private requestQueue: RpcRequest[] = [];

  private subscriptions: Record<string, Function> = {};

  private activeRequest: RpcRequest | null = null;

  private lastActivityAt: number | null = null;

  public constructor(
    private url: string,
    private callbacks?: { onLost?: Function; onReconnect?: Function },
    private keepAlive?: {
      time: number;
      ping?: {
        request: RpcRequest;
        interval: number;
      };
    }
  ) {
    this.open();
  }

  private pingTimer: number | null = null;
  private clearPingTimer() {
    if (this.pingTimer) {
      window.clearTimeout(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private setLastActivityAt() {
    this.lastActivityAt = +moment();

    if (this.keepAlive) {
      this.clearPingTimer();

      this.pingTimer = window.setTimeout(
        () => this.sendRequest(this.keepAlive!.ping!.request),
        this.keepAlive!.ping!.interval * 1000
      );
    }
  }

  private unsetLastActivityAt() {
    this.lastActivityAt = null;
    this.clearPingTimer();
  }

  private open() {
    this.tries++;
    this.socket = new WebSocket(this.url);

    const socket = this.socket;

    const isSocketActual = (): boolean => {
      if (socket !== this.socket) {
        console.error("Socket is not actual: ", socket, this.socket);
        return false;
      }
      return true;
    };

    socket.onopen = () => {
      if (!isSocketActual()) {
        return;
      }

      this.clearResponseBuffer();
      this.activeRequest = null;
      this.setLastActivityAt();

      this.status = WebSocket.OPEN;

      if (this.tries > 1 && this.callbacks?.onReconnect) {
        this.callbacks.onReconnect();
      }

      if (this.requestQueue.length) {
        this.sendQueuedRequest();
      }
    };
    socket.onclose = (event: CloseEvent) => {
      if (!isSocketActual()) {
        return;
      }

      this.unsetLastActivityAt();

      if (this.status === WebSocket.CLOSING) {
        console.log(`Connection to ${this.url} has been closed`);
        this.status = WebSocket.CLOSED;
      } else {
        this.onConnectionError();
      }
    };

    socket.onmessage = (event: MessageEvent) => {
      if (!isSocketActual()) {
        return;
      }

      this.setLastActivityAt();

      const dataChunk = event.data as string;

      this.responseBuffer.push(dataChunk);
      this.responseBufferContentLength += this.getContentLength(
        this.responseBuffer.length === 1 ? this.getResponseContent() : dataChunk
      );

      const contentLength = this.getHeader("Content-Length");

      const status = this.getResponseStatus();
      const statusCode = this.getResponseStatusCode();

      if (
        (contentLength !== null &&
          this.responseBufferContentLength >= +contentLength) ||
        (contentLength === null &&
          statusCode &&
          _.inRange(statusCode, 500, 502 + 1))
      ) {
        const content = this.getResponseContent();
        let parsedContent: null | string | number | object = content;
        try {
          parsedContent = JSON.parse(content);
        } catch (e) {
          /*empty*/
        }

        const signal = this.getHeader("X-Colibri-Signal");
        const service = this.getHeader("X-Colibri-Service");

        this.clearResponseBuffer();

        if (status === "100 Signal") {
          // signal received

          if (`/${service}/${signal}` in this.subscriptions) {
            this.subscriptions[`/${service}/${signal}`](parsedContent);
          }
        } else if (this.activeRequest) {
          if (statusCode && _.inRange(statusCode, 200, 205 + 1)) {
            const isSuccess = !(
              parsedContent !== null &&
              typeof parsedContent === "object" &&
              ".error" in parsedContent
            );
            if (isSuccess) {
              this.activeRequest.callback(parsedContent);
            } else if (this.activeRequest.errorCallback) {
              parsedContent &&
              typeof parsedContent === "object" &&
              ".error" in parsedContent
                ? this.activeRequest.errorCallback(
                    _.get(parsedContent, ".error")
                  )
                : this.activeRequest.errorCallback();
            }
          } else if (
            statusCode &&
            _.inRange(statusCode, 400, 502 + 1) &&
            this.activeRequest.errorCallback
          ) {
            this.activeRequest.errorCallback(statusCode);
          }

          this.requestQueue = _.filter(
            this.requestQueue,
            r => r !== this.activeRequest
          );
          this.activeRequest = null;

          if (this.requestQueue.length) {
            this.sendQueuedRequest();
          }
        }
      }
    };
  }

  private getContentLength(content: string) {
    return encodeURI(content).split(/%..|./).length - 1;
  }

  private onConnectionError() {
    const isClosedBeforeOpening = this.status === WebSocket.CONNECTING;

    this.status = WebSocket.CLOSED;
    console.error(
      `Connection to ${this.url} has been lost. Will try to reconnect in 1 second`
    );

    if (this.callbacks?.onLost && !isClosedBeforeOpening) {
      this.callbacks.onLost();
    }

    setTimeout(() => {
      console.error(`Reconnecting to ${this.url} ...`);
      this.open();
    }, 1000);
  }

  public close() {
    if (this.status !== WebSocket.OPEN) {
      throw new Error("Connection not established");
    }
    this.status = WebSocket.CLOSING;
    this.socket?.close();
    this.unsetLastActivityAt();
  }

  private sendQueuedRequest() {
    // @todo: Find a reason why request can be sent before previous one`s answer
    if (this.activeRequest) {
      console.log("An active request exists. Current request is ignored");
      return;
    }

    if (
      this.keepAlive?.time &&
      this.lastActivityAt &&
      moment().unix() - this.lastActivityAt > this.keepAlive.time
    ) {
      this.close();
      this.open();
      return;
    }

    this.activeRequest = this.requestQueue[0];

    const bodyContent = this.activeRequest.body
      ? typeof this.activeRequest.body === "string"
        ? this.activeRequest.body
        : JSON.stringify(this.activeRequest.body)
      : "";

    const text = `POST ${this.activeRequest.method} HTTP/1.1
Host: ${this.activeRequest.host}
Connection: keep-alive
Content-Length: ${this.getContentLength(bodyContent)}
Accept: application/json
User-Agent: REAP
Content-Type: application/json${
      this.activeRequest.jwt
        ? `\r\nAuthorization: Bearer ${this.activeRequest.jwt}`
        : ""
    }${
      this.activeRequest.headers
        ? _.reduce(
            this.activeRequest.headers,
            (res, v, k) => {
              return `${res ?? ""}\r\n${k}: ${v}`;
            },
            ""
          )
        : ""
    }\r\n\r\n${bodyContent}`;

    _.map(_.chunk(text.split(""), 32 * 1024), v => v.join("")).forEach(t => {
      this.socket?.send(t);
      this.setLastActivityAt();
    });
  }

  public sendRequest(request: RpcRequest) {
    request.isPrioritized
      ? this.requestQueue.splice(
          _.findLastIndex(this.requestQueue, req => !!req.isPrioritized) + 1,
          0,
          request
        )
      : this.requestQueue.push(request);

    if (this.requestQueue.length === 1 && this.status === WebSocket.OPEN) {
      this.sendQueuedRequest();
    }
  }

  public subscribe(event: string, callback: Function) {
    this.subscriptions[event] = callback;
  }

  public unsubscribe(event: string) {
    delete this.subscriptions[event];
  }

  private getResponseStatus(): string | null {
    const status = _.trim(
      (this.responseBuffer[0] ?? "").split(/\n/)[0].slice("HTTP/1.1 ".length)
    );

    return status !== "" ? status : null;
  }

  private getResponseStatusCode(): number | null {
    const status = this.getResponseStatus();

    return status ? parseInt(status) : null;
  }

  private getResponseContent(): string {
    return this.responseBuffer
      .join("")
      .split(/\r\n\r\n/)
      .slice(1)
      .join("\r\n\r\n");
  }

  private getHeader(name: string): string | null {
    const headers = (this.responseBuffer[0] ?? "")
      .split(/\r\n\r\n/)[0]
      .split(/\n/);

    const header = _.find(headers, h => _.startsWith(h, `${name}:`));

    return header ? _.trim(header.slice(`${name}:`.length)) : null;
  }
}

export class WsClient {
  private static instance: WsClient = new WsClient();

  private constructor() {
    /*empty*/
  }

  public static getInstance() {
    return this.instance;
  }

  private connections: { [key: string]: Connection } = {};

  public openConnection(
    url: string,
    callbacks?: { onLost?: Function; onReconnect?: Function },
    keepAlive?: {
      time: number;
      ping?: {
        request: RpcRequest;
        interval: number;
      };
    }
  ): Connection {
    const existingConnection = this.getConnection(url);
    if (existingConnection?.status === WebSocket.CONNECTING) {
      throw new Error("Connection is opening...");
    }
    if (existingConnection?.status === WebSocket.OPEN) {
      throw new Error("Connection has been already opened");
    }
    const connection = new Connection(url, callbacks, keepAlive);
    this.connections[url] = connection;
    return connection;
  }

  public closeConnection(url: string) {
    const connection = this.getConnection(url);
    if (connection?.status !== WebSocket.OPEN) {
      throw new Error("Connection not established");
    }
    connection.close();
    delete this.connections[url];
  }

  public getConnection(url: string): Connection | null {
    const connection = this.connections[url];
    if (
      connection &&
      [WebSocket.OPEN, WebSocket.CONNECTING].includes(connection.status)
    ) {
      return connection;
    }
    return null;
  }
}
